/**
 * @file: feedThreadDescriptor.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Thread-row rendering metadata. Driven by `meta.scope`:
 *  - order               → vehicle/customer headline
 *  - rewards             → member account
 *  - maintenance-plans   → personal reminder thread
 *  - system              → platform messages
 *
 * The "subtitle" is the latest message's title (preview line) — derived
 * from `descriptorFor(latestMessage.params)`.
 */

import {
  FeedMediaSnapshot,
  FeedThreadInboxItem,
  FeedThreadMeta,
  FeedThreadScope,
} from '@euler/model/feed';
import { parseTagsFromString } from '@euler/utils';
import { descriptorFor } from './feedMessageDescriptor';

export interface FeedThreadDescriptor {
  /** Headline for the row (vehicle plate, member name, "系统通知", ...). */
  title: string;
  /** Secondary chip (customer name, vehicle model, scope label). */
  meta?: string;
  /** Preview line — last message's headline. */
  preview: string;
  /** Icomoon icon for the leading avatar (fallback when no logo). */
  icon: string;
  /** Color accent for the leading avatar's background. */
  accent: string;
  /**
   * Brand logo URL when the thread has one (order threads, rear-enriched).
   * The ThreadRow prefers this over the icon — falls back to icon+accent
   * when null. Sourced from `meta.vehicleBrandLogo`.
   */
  brandLogo?: string;
  /**
   * Localized order-creation date label for order threads (e.g.
   * `建单 5/13 04:34`). Undefined for non-order threads or when the
   * source `meta.orderCreatedAt` is missing.
   */
  orderDateLabel?: string;
  /** Order-tag chips parsed from `meta.tags`. Empty when none. */
  tags?: string[];
  /**
   * Latest message's media preview-strip, propagated from
   * `descriptorFor(latestMessage.params).medias`. Currently only
   * task-flow status updates carry these. Renders as a thumbnail row
   * beside the (otherwise text-only and bland) preview line.
   */
  medias?: FeedMediaSnapshot[];
  /**
   * Optional color for the preview line, propagated from the latest
   * message's descriptor. Used for exceptional events (e.g. red for
   * order rollback).
   */
  previewAccent?: string;
  /**
   * When true, the preview line wraps fully instead of clamping to 2
   * lines + ellipsis. Maintenance-plan reminders pack date / mileage /
   * due-countdown into the preview, so truncating loses the countdown.
   */
  previewWrap?: boolean;
  /** Whether the thread is "sticky-top". Mirrors layout requirement. */
  pinnedByScope: boolean;
}

const accents = {
  blue: '#1f80ff',
  green: '#34c759',
  amber: '#ff9500',
  red: '#ff3b30',
  purple: '#af52de',
  teal: '#0fb5ae',
  grey: '#8e8e93',
};

const kAlwaysOnTop: ReadonlySet<FeedThreadScope> = new Set([
  FeedThreadScope.System,
  FeedThreadScope.MaintenancePlans,
  FeedThreadScope.Leads,
  FeedThreadScope.MarketingFollowup,
  FeedThreadScope.Rewards,
]);

export function isPinnedByScope(scope: FeedThreadScope): boolean {
  return kAlwaysOnTop.has(scope);
}

/**
 * Theming tier for the inbox row. Three buckets so the list reads
 * top-down by importance:
 *  - `default-pinned`: scope-pinned essentials the user can't unpin
 *    (`maintenance-plans`, `rewards`). Always-on-top by design.
 *  - `other-pinned`: `system` notices and user-pinned order threads
 *    (`pinnedAt != null`) — also live above unpinned threads but get
 *    a distinct shade so the essentials still stand out.
 *  - `unpinned`: regular paginated order threads.
 *
 * Mirrors the WeChat-style tiering the chat list uses; same hierarchy,
 * one more tier since feed has both scope-pinned + user-pinned.
 */
export type FeedThreadTier = 'default-pinned' | 'other-pinned' | 'unpinned';

export function feedThreadTier(thread: FeedThreadInboxItem): FeedThreadTier {
  if (
    thread.scope === FeedThreadScope.MaintenancePlans ||
    thread.scope === FeedThreadScope.Leads ||
    thread.scope === FeedThreadScope.MarketingFollowup ||
    thread.scope === FeedThreadScope.Rewards
  ) {
    return 'default-pinned';
  }
  if (thread.scope === FeedThreadScope.System || thread.pinnedAt != null) {
    return 'other-pinned';
  }
  return 'unpinned';
}

export function isFeedThreadPinned(thread: FeedThreadInboxItem): boolean {
  return feedThreadTier(thread) !== 'unpinned';
}

/**
 * Order the paginated (non-scope-pinned) threads the way the server does
 * — `pinned_at is null, pinned_at desc, latest_activity_at desc, id desc`.
 * Applied client-side so an optimistic pin/unpin re-sorts the loaded set
 * before the next refresh confirms it.
 */
export function compareFeedThreadsForList(
  a: FeedThreadInboxItem,
  b: FeedThreadInboxItem,
): number {
  const aPinned = a.pinnedAt != null;
  const bPinned = b.pinnedAt != null;
  if (aPinned !== bPinned) return aPinned ? -1 : 1;
  if (aPinned && bPinned) {
    const byPin =
      new Date(b.pinnedAt as string).getTime() -
      new Date(a.pinnedAt as string).getTime();
    if (byPin !== 0) return byPin;
  }
  const byActivity =
    new Date(b.latestActivityAt).getTime() -
    new Date(a.latestActivityAt).getTime();
  if (byActivity !== 0) return byActivity;
  // ULID id desc — stable tiebreaker matching the server's deterministic
  // pagination order.
  return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
}

/**
 * Whether a thread's messages stay unread until the user acts on each
 * one, rather than being bulk-marked read on open. Maintenance-plan
 * reminders are actionable work items (convert-to-lead / mark-read), so
 * opening the thread must NOT clear them — every other scope auto-reads.
 */
export function threadUsesManualRead(scope: FeedThreadScope): boolean {
  return (
    scope === FeedThreadScope.MaintenancePlans ||
    scope === FeedThreadScope.Leads ||
    scope === FeedThreadScope.MarketingFollowup
  );
}

export function descriptorForThread(
  thread: FeedThreadInboxItem,
): FeedThreadDescriptor {
  const latestDesc = thread.latestMessage
    ? descriptorFor(thread.latestMessage.params)
    : undefined;
  const preview = [latestDesc?.title, latestDesc?.subtitle]
    .filter((s): s is string => !!s && s.length > 0)
    .join(': ');
  const medias = latestDesc?.medias;
  const previewAccent = latestDesc?.previewAccent;

  switch (thread.scope) {
    case FeedThreadScope.Order:
      return describeOrderThread(thread, preview, medias, previewAccent);
    case FeedThreadScope.Rewards:
      return describeRewardsThread(thread, preview);
    case FeedThreadScope.System:
      return {
        title: '系统通知',
        preview,
        icon: 'information-solid',
        accent: accents.blue,
        pinnedByScope: true,
      };
    case FeedThreadScope.MaintenancePlans:
      return {
        title: '保养提醒',
        meta: '到期维保计划',
        preview,
        icon: 'notification',
        accent: accents.amber,
        previewWrap: true,
        pinnedByScope: true,
      };
    case FeedThreadScope.MarketingFollowup:
      return {
        title: '营销跟进',
        meta: '客户回复与草稿审核',
        preview,
        icon: 'notification',
        accent: accents.teal,
        previewWrap: true,
        pinnedByScope: true,
      };
    case FeedThreadScope.Leads:
      return {
        title: '商机提醒',
        meta: '待跟进线索',
        preview,
        icon: 'notification',
        accent: accents.purple,
        previewWrap: true,
        pinnedByScope: true,
      };
  }
}

function describeOrderThread(
  thread: FeedThreadInboxItem,
  preview: string,
  medias: FeedMediaSnapshot[] | undefined,
  previewAccent: string | undefined,
): FeedThreadDescriptor {
  const meta = thread.meta as
    | (FeedThreadMeta & { scope: FeedThreadScope.Order })
    | null;
  const title = meta?.licensePlateNo ?? `工单 ${meta?.orderNo ?? ''}`.trim();
  const subtitleParts: string[] = [];
  if (meta?.customerName) subtitleParts.push(meta.customerName);
  if (meta?.vehicleName ?? meta?.vehicleModel) {
    subtitleParts.push(meta.vehicleName ?? (meta?.vehicleModel as string));
  }
  return {
    title,
    meta: subtitleParts.join(' · ') || undefined,
    preview,
    icon: 'notification',
    accent: accents.teal,
    brandLogo: meta?.vehicleBrandLogo,
    orderDateLabel: formatOrderCreatedAt(meta?.orderCreatedAt),
    tags: parseTags(meta?.tags),
    medias,
    previewAccent,
    pinnedByScope: false,
  };
}

function parseTags(raw: string | undefined): string[] {
  if (!raw) return [];
  return parseTagsFromString(raw);
}

function formatOrderCreatedAt(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `建单 ${m}/${day} ${hh}:${mm}`;
}

function describeRewardsThread(
  _thread: FeedThreadInboxItem,
  preview: string,
): FeedThreadDescriptor {
  return {
    title: '我的账户',
    meta: '推广收益与提现',
    preview,
    icon: 'information-solid',
    accent: accents.green,
    pinnedByScope: true,
  };
}
